import React, { forwardRef, useRef, useImperativeHandle } from 'react'
import FieldContainer from "./components/FieldContainer"
import InputContainer from "./components/InputContainer"
import PropTypes from 'prop-types'
import NumberFormat from 'react-number-format'
import { fieldContainerProps, fieldProps, inputProps, badgeProps } from './commonProps'
import useInternalState from './components/useInternalState'
import withAnalytics from './components/withAnalytics'
import useAutoFocus from './components/useAutoFocus'

const toNumericalString = (value) => value.replace(/[^0-9|.|-]+/g, '')

/** Field for number value inputs */
const Number = forwardRef(({
  id,
  error,
  warning,
  touched,
  focus,
  placeholder,
  name,
  label,
  initialValue,
  className,
  inputRef,
  inputProps,
  disabled,
  autoComplete,
  sensitive,
  maxLength,
  leftBadge,
  rightBadge,
  prefix,
  suffix,
  decimalScale,
  allowNegative,
  fixedDecimalScale,
  autoFocus,
  autoFocusDelay,
  maxAllowedValue: maxAllowedValueProp,
  thousandSeparator,
  hideCheckIcon,
  appearance
}, ref) => {
  const { internalFocus, internalTouched, internalInputProps } = useInternalState({ focus, touched, inputProps }),
    managedInputRef = useRef(null),
    hasError = internalTouched && !!error,
    maxAllowedValue = maxAllowedValueProp || parseInt(new Array(maxLength + 1).join('9')),
    filled = !!((managedInputRef.current && managedInputRef.current.value) || inputProps.value)

  useImperativeHandle(inputRef, () => managedInputRef.current)

  useAutoFocus(managedInputRef, autoFocus, autoFocusDelay)

  return (
    <FieldContainer ref={ref} id={id} error={error} warning={warning} touched={internalTouched} focus={internalFocus} className={className} appearance={appearance}>
      <InputContainer label={label} hasError={hasError} filled={filled} disabled={disabled} focus={internalFocus} sensitive={sensitive} leftBadge={leftBadge} rightBadge={rightBadge} hideCheckIcon={hideCheckIcon} appearance={appearance}>
        {({inputStyles}) =>
          <NumberFormat
            type="tel"
            {...internalInputProps}
            isAllowed={({floatValue}) => floatValue === undefined || floatValue <= maxAllowedValue}
            name={name}
            id={id}
            thousandSeparator={thousandSeparator}
            prefix={prefix}
            suffix={suffix}
            decimalScale={decimalScale}
            isNumericString={true}
            allowNegative={allowNegative}
            getInputRef={el => managedInputRef.current = el}
            autoComplete={autoComplete ? 'on' : 'off'}
            className={inputStyles}
            placeholder={placeholder}
            disabled={disabled}
            onChange={e => {
              e.target.value = toNumericalString(e.target.value)
              internalInputProps.onChange && internalInputProps.onChange(e)
            }}
            onBlur={e => {
              e.target.value = toNumericalString(e.target.value)
              internalInputProps.onBlur && internalInputProps.onBlur(e)
            }}
            fixedDecimalScale={fixedDecimalScale}
            defaultValue={inputProps.value === undefined ? initialValue : undefined}
            data-testid="inputElement"
          />
        }
       </InputContainer>
    </FieldContainer>
  )
})

Number.propTypes = {
  ...fieldContainerProps,
  ...fieldProps,
  ...inputProps,
  ...badgeProps,
  /** A prefix to be shown before the number */
  prefix: PropTypes.string,
  /** A suffix to be shown after the number */
  suffix: PropTypes.string,
  /** Quantity of decimals allowed */
  decimalScale: PropTypes.number,
  /** Indicates if negative numbers can be provided */
  allowNegative: PropTypes.bool,
  /** If set to true, enforces the provision of the decimal part of the number */
  fixedDecimalScale: PropTypes.bool,
  /** Prevents user from entering number greater than set by this prop */
  maxAllowedValue: PropTypes.number,
  /** Allow to add thousands comma separator */
  thousandSeparator: PropTypes.bool,
}

Number.defaultProps = {
  error: null,
  warning: null,
  touched: null,
  placeholder: null,
  label: null,
  initialValue: '',
  maxLength: 9,
  focus: null,
  inputRef: null,
  inputProps: {},
  disabled: false,
  autoComplete: true,
  sensitive: false,
  leftBadge: null,
  rightBadge: null,
  prefix: '',
  suffix: '',
  decimalScale: 0,
  allowNegative: false,
  fixedDecimalScale: false,
  autoFocus: false,
  autoFocusDelay: 500,
  maxAllowedValue: null,
  thousandSeparator: true,
  hideCheckIcon: false
}

const formatAnalyticsValue = (value) => {
  if (value === undefined) {
    return
  }

  let formatedValue = value.replace(/[^0-9|.|-]+/g, '')

  if (formatedValue.includes('.')) {
    return parseFloat(formatedValue)
  } else {
    return parseInt(formatedValue)
  }
}

export default withAnalytics(Number, {disabled: false, events: ['onBlur'], format: formatAnalyticsValue})
